import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Palette, Upload, Save, X, Loader2, ImageIcon } from 'lucide-react';
import api from '../../lib/api';

const PRESET_COLORS = ['#D4AF37', '#F87171', '#8B5CF6', '#34D399', '#38BDF8', '#FF6EC7', '#F97316', '#FACC15'];

export const BrandingPanel = ({ content, setContent, brandingApi }) => {
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const logoInput = useRef(null);
  const bannerInput = useRef(null);
  const socials = content.socials || {};

  useEffect(() => {
    if (!brandingApi) return;
    api.get(brandingApi).then(r => {
      const b = r.data || {};
      setContent(c => ({
        ...c,
        logo_url: c.logo_url || b.logo_url || undefined,
        accent_override: c.accent_override || b.accent || undefined,
        socials: c.socials && Object.values(c.socials).some(Boolean)
          ? c.socials
          : { instagram: b.instagram || '', spotify: b.spotify || '', tiktok: b.tiktok || '', website: b.website || '' },
      }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandingApi]);

  const upload = async (file, field) => {
    if (!file) return;
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/upload/image?folder=email_branding', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setContent(c => ({ ...c, [field]: r.data.url }));
      toast.success(field === 'logo_url' ? 'Logo subido' : 'Banner subido');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al subir la imagen');
    } finally {
      setUploading(null);
    }
  };

  const setSocial = (k, v) => setContent(c => ({ ...c, socials: { ...(c.socials || {}), [k]: v } }));

  const saveBranding = async () => {
    if (!brandingApi) return;
    setSaving(true);
    try {
      await api.put(brandingApi, {
        logo_url: content.logo_url || '',
        accent: content.accent_override || '',
        instagram: socials.instagram || '',
        spotify: socials.spotify || '',
        tiktok: socials.tiktok || '',
        website: socials.website || '',
      });
      toast.success('Branding guardado — se pre-carga en tus próximas campañas');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar el branding');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="branding-panel">
      {/* Logo */}
      <div>
        <label className="text-xs text-zinc-400 uppercase flex items-center gap-1.5 mb-2">
          <ImageIcon size={11} className="text-[#D4AF37]" /> Logo de tu marca
        </label>
        <div className="flex items-center gap-3">
          {content.logo_url ? (
            <div className="relative">
              <img src={content.logo_url} alt="logo" className="h-10 max-w-[120px] object-contain rounded bg-zinc-800 p-1" />
              <button
                type="button"
                onClick={() => setContent(c => ({ ...c, logo_url: undefined }))}
                className="absolute -top-2 -right-2 bg-red-500 rounded-full p-0.5 text-white"
                data-testid="remove-logo-btn"
              >
                <X size={10} />
              </button>
            </div>
          ) : (
            <span className="text-[11px] text-zinc-500 italic">Sin logo</span>
          )}
          <button
            type="button"
            onClick={() => logoInput.current?.click()}
            disabled={uploading === 'logo_url'}
            className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-md text-xs flex items-center gap-1.5"
            data-testid="upload-logo-btn"
          >
            {uploading === 'logo_url' ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {content.logo_url ? 'Cambiar' : 'Subir logo'}
          </button>
          <input ref={logoInput} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], 'logo_url')} />
        </div>
      </div>

      {/* Accent color */}
      <div>
        <label className="text-xs text-zinc-400 uppercase flex items-center gap-1.5 mb-2">
          <Palette size={11} className="text-[#D4AF37]" /> Color de acento
        </label>
        <div className="flex items-center gap-1.5 flex-wrap">
          {PRESET_COLORS.map(col => (
            <button
              key={col}
              type="button"
              onClick={() => setContent(c => ({ ...c, accent_override: col }))}
              className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${content.accent_override === col ? 'border-white scale-110' : 'border-transparent'}`}
              style={{ background: col }}
              data-testid={`accent-preset-${col.replace('#', '')}`}
            />
          ))}
          <input
            type="color"
            value={content.accent_override || '#D4AF37'}
            onChange={(e) => setContent(c => ({ ...c, accent_override: e.target.value }))}
            className="w-8 h-6 rounded cursor-pointer bg-transparent border border-zinc-700"
            title="Color personalizado"
            data-testid="accent-color-input"
          />
          {content.accent_override && (
            <button
              type="button"
              onClick={() => setContent(c => ({ ...c, accent_override: undefined }))}
              className="text-[10px] text-zinc-500 hover:text-white underline ml-1"
              data-testid="accent-reset-btn"
            >
              Restaurar
            </button>
          )}
        </div>
      </div>

      {/* Banner image */}
      <div>
        <label className="text-xs text-zinc-400 uppercase flex items-center gap-1.5 mb-2">
          <ImageIcon size={11} className="text-[#D4AF37]" /> Imagen banner (independiente del flyer)
        </label>
        <div className="flex items-center gap-3">
          {content.banner_image ? (
            <div className="relative">
              <img src={content.banner_image} alt="banner" className="h-10 w-20 object-cover rounded" />
              <button
                type="button"
                onClick={() => setContent(c => ({ ...c, banner_image: undefined }))}
                className="absolute -top-2 -right-2 bg-red-500 rounded-full p-0.5 text-white"
                data-testid="remove-banner-btn"
              >
                <X size={10} />
              </button>
            </div>
          ) : (
            <span className="text-[11px] text-zinc-500 italic">{content.event_image ? 'Usando flyer del evento' : 'Sin banner'}</span>
          )}
          <button
            type="button"
            onClick={() => bannerInput.current?.click()}
            disabled={uploading === 'banner_image'}
            className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-md text-xs flex items-center gap-1.5"
            data-testid="upload-banner-btn"
          >
            {uploading === 'banner_image' ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {content.banner_image ? 'Cambiar' : 'Subir banner'}
          </button>
          <input ref={bannerInput} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], 'banner_image')} />
        </div>
      </div>

      {/* Socials */}
      <div>
        <label className="text-xs text-zinc-400 uppercase mb-2 block">Redes sociales (aparecen al pie del mail)</label>
        <div className="grid grid-cols-2 gap-2">
          {[
            ['instagram', 'Instagram (@usuario o URL)'],
            ['spotify', 'Spotify (URL)'],
            ['tiktok', 'TikTok (URL)'],
            ['website', 'Sitio web'],
          ].map(([k, ph]) => (
            <input
              key={k}
              type="text"
              value={socials[k] || ''}
              onChange={(e) => setSocial(k, e.target.value)}
              placeholder={ph}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-2.5 py-1.5 text-white text-xs"
              data-testid={`social-${k}-input`}
            />
          ))}
        </div>
      </div>

      {brandingApi && (
        <button
          type="button"
          onClick={saveBranding}
          disabled={saving}
          className="w-full px-3 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5"
          data-testid="save-branding-btn"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          Guardar como mi marca (logo + color + redes)
        </button>
      )}
    </div>
  );
};
